import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormInputDate } from '@/components/forms/form-input-date';
import { FormMultiSelect } from '@/components/forms/form-multi-select';
import { FormRadioGroup } from '@/components/forms/form-radio-group';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { FormTextArea } from '@/components/forms/form-text-area';
import { Label } from '@/components/ui/label';
import { MultiSelectOption } from '@/components/ui/multi-select';
import {
  buildCoachSelectOptions,
  buildNameLikeSelectOptions,
  comboBoxOptionsOf,
  emptySelectOption,
} from '@/components/ui/multi-select-utils';
import { useFeatureFlag } from '@/hooks/use-feature-flags';
import {
  coachesTypeaheadSearch,
  useAllLearningPaths,
  useAvailableCoachesSearch,
  useClassTimeOptions,
  userTypeaheadSearch,
} from '@/lib/api-call-hooks';
import { EmptyArray, getCoach, getCoaches } from '@/lib/api-calls';
import { AllSelectTeacherTypes, AllUpdateTeacherTypeValues, CoachesTypeaheadSearchOptions } from '@/lib/coach-utils';
import { formCreateEditClassSchema, getAttendanceStatusLabel, emailsSentOptions } from '@/lib/student-group-utils';
import {
  formatPlainDate,
  FrequentTimeZones,
  isNotNullish,
  OneTimeOnboardingLearningPathNames,
} from '@airreading/common';
import {
  attendanceStatusSelectableValues,
  ClassEligibility,
  CoachStatus,
  GetCoachesModifier,
  OnboardingLearningPathName,
  StudentGroupWithScheduleAndLevel,
} from '@airreading/common/data';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

export interface CreateEditClassFormProps {
  schoolId: string;
  form: UseFormReturn<z.infer<typeof formCreateEditClassSchema>>;
  from?: StudentGroupWithScheduleAndLevel;
  fromAttendance?: boolean;
}

export const CreateEditClassForm = ({ schoolId, form, from, fromAttendance = false }: CreateEditClassFormProps) => {
  const isOneTimeClass = from?.classTime.type === 'one_time';
  const queryClient = useQueryClient();
  const { watch, setValue } = form;
  const selectedScheduleId = watch('schedule').value;
  const selectedClassTimeId = watch('classTime').value;
  const overrideStartPlainDate = watch('overrideStartPlainDate');
  const overrideEndPlainDate = watch('overrideEndPlainDate');
  const teacherAvailabilityV2Enabled = useFeatureFlag('teacher-availability-v2', false);

  const {
    data: schedules,
    classTimes,
    isPending: isLoadingSchedules,
    classTimeOptions,
  } = useClassTimeOptions({
    schoolId,
  });
  const scheduleOptions: MultiSelectOption[] = useMemo(
    () => schedules?.map(buildNameLikeSelectOptions) || EmptyArray,
    [schedules],
  );
  const selectedSchedule = schedules?.find((s) => s.id === selectedScheduleId);

  const filteredClassTimeOptions = useMemo(() => {
    if (!selectedSchedule) return [];

    return classTimeOptions.filter((ct) =>
      selectedSchedule.classTimes.some(({ id: scheduleClassTimeId }) => scheduleClassTimeId === ct.value),
    );
  }, [classTimeOptions, selectedSchedule]);

  const selectedClassTime = classTimes?.find((ct) => ct.classTime.id === selectedClassTimeId)?.classTime;

  const { learningPaths: allLearningPaths, isPending: isLoadingLearningPaths } = useAllLearningPaths();
  const availableLearningPaths = allLearningPaths.filter(
    // onboarding lessons/learning paths cannot be set from class form. This form
    // is only for recurring classes
    (lp) => !OneTimeOnboardingLearningPathNames.includes(lp.name as OnboardingLearningPathName),
  );
  const learningPathOptions: MultiSelectOption[] = availableLearningPaths.map(buildNameLikeSelectOptions);

  const selectedComboLearningPathId = watch('learningPath').value;
  const selectedLearningPath = availableLearningPaths.find((lp) => lp.id === selectedComboLearningPathId);
  const selectableLessons: MultiSelectOption[] = (selectedLearningPath?.learningPathLessons || []).map(
    ({ lesson }) => ({
      label: lesson.name,
      value: lesson.id,
    }),
  );
  const [selectedTeacherType, setSelectedTeacherType] = useState<string | undefined>(
    AllUpdateTeacherTypeValues.Available,
  );

  useEffect(() => {
    const sub = watch((value, { name }) => {
      if (name === 'schedule' && value.classTime?.value) {
        setValue('classTime', emptySelectOption, { shouldDirty: true, shouldValidate: true });
      }

      if (name === 'learningPath' && value.startingLesson?.value) {
        setValue('startingLesson', emptySelectOption, { shouldDirty: true, shouldValidate: true });
      }
    });
    return () => sub.unsubscribe();
  }, [setValue, watch, form]);

  useEffect(() => {
    // This effect is only for editing existing classes
    if (!from) {
      return;
    }

    // Reset coach dropdowns when teacher type changes
    const sub = watch((value, { name }) => {
      if (name === 'teacherType' && value.teacherType) {
        setValue('coachAvailable', emptySelectOption, { shouldDirty: true, shouldValidate: true });
        setValue('coachAll', emptySelectOption, { shouldDirty: true, shouldValidate: true });

        setSelectedTeacherType(value.teacherType);
        queryClient.invalidateQueries(getCoaches());
      }
    });
    return () => sub.unsubscribe();
  }, [setValue, watch, form, from, queryClient]);

  const classEligibilities = selectedClassTime
    ? [ClassEligibility.can_teach_intervention]
    : [ClassEligibility.can_teach_diagnostic, ClassEligibility.can_teach_practice];
  const now = Temporal.Now.plainDateTimeISO(FrequentTimeZones.UsCentral);

  const coachesSearchOptions: GetCoachesModifier =
    selectedSchedule && selectedClassTimeId
      ? {
          pageIndex: 0,
          filters: {
            availabilities: {
              classTimes: [selectedClassTimeId],
              orgId: classTimes[0].schedule.school.org.id,
              classEligibilities,
              overrideStartPlainDate: overrideStartPlainDate ?? Temporal.PlainDate.from(now.toString()),
              overrideEndPlainDate: overrideEndPlainDate ?? Temporal.PlainDate.from(selectedSchedule.endPlainDate),
              shouldAvoidConflicts: teacherAvailabilityV2Enabled && isOneTimeClass,
            },
          },
          isNewLogic: teacherAvailabilityV2Enabled,
        }
      : {
          pageIndex: 0,
        };

  const coachFilters: CoachesTypeaheadSearchOptions = {
    filters: {
      statuses: [CoachStatus.ready_to_teach],
    },
  };
  const allCoachesSearchOptions: CoachesTypeaheadSearchOptions = {
    shouldDisplayOverallGrade: true,
    ...coachFilters,
  };

  const { data, isLoading: isLoadingCoaches } = useAvailableCoachesSearch(coachesSearchOptions, {
    shouldDisplayOverallGrade: true,
  });

  // Add selected coach to available coaches (in case is not available, but still is the original coach)
  const selectedCoachId = from ? from.coach?.id || watch('coach')?.value : null;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data: originalCoach } = selectedCoachId ? useQuery(getCoach(selectedCoachId)) : { data: null };

  const availableCoaches = useMemo(() => {
    const baseCoaches = data || [];

    if (originalCoach && !baseCoaches.some((coach) => coach.value === originalCoach.id)) {
      return [...baseCoaches, buildCoachSelectOptions(originalCoach)];
    }

    return baseCoaches;
  }, [data, originalCoach]);

  const teacherSelect =
    selectedTeacherType === AllUpdateTeacherTypeValues.Available ? (
      <FormComboBox
        form={form}
        label={'Available teachers'}
        name={'coachAvailable'}
        placeholder="Select teacher"
        {...comboBoxOptionsOf(availableCoaches)}
        shouldHideCheck={true}
        renderOptionSelected={(option) => (option ? option.label : 'Select a teacher')}
        emptySearchOptionMessage="No available teachers"
        isLoadingKnownOptions={isLoadingCoaches}
      />
    ) : (
      <FormComboBox
        form={form}
        label={'All teachers'}
        name={'coachAll'}
        placeholder="Select teacher"
        {...comboBoxOptionsOf(coachesTypeaheadSearch(queryClient, allCoachesSearchOptions))}
        shouldHideCheck={true}
        renderOptionSelected={(option) => (option ? option.label : 'Select a teacher')}
      />
    );

  return (
    <div className="grid grid-cols-2 gap-2 py-4">
      {!fromAttendance ? (
        <>
          {!from && (
            <>
              <FormComboBox
                form={form}
                label={'Add teacher'}
                name="coach"
                placeholder="Select teacher"
                {...comboBoxOptionsOf(coachesTypeaheadSearch(queryClient))}
              />
              <FormMultiSelect
                form={form}
                label={'Add students'}
                name="students"
                multipleSelectorProps={{
                  placeholder: 'Search students by name to add to the group',
                  onSearch: userTypeaheadSearch(queryClient, { schools: [schoolId].filter(isNotNullish) }),
                }}
              />
            </>
          )}
          {!isOneTimeClass && (
            <div className="col-span-2">
              <FormComboBox
                form={form}
                label="Schedule"
                name="schedule"
                placeholder="Select schedule"
                {...comboBoxOptionsOf(scheduleOptions)}
                triggerProps={{
                  disabled: isLoadingSchedules,
                }}
                formDescription={
                  selectedSchedule ? (
                    <div>
                      Dates: {formatPlainDate(selectedSchedule.startPlainDate)} -{' '}
                      {formatPlainDate(selectedSchedule.endPlainDate)}
                    </div>
                  ) : null
                }
              />
            </div>
          )}
          <div className="col-span-2">
            <FormComboBox
              form={form}
              label="Class time (CT)"
              name="classTime"
              placeholder="Select class times"
              {...comboBoxOptionsOf(filteredClassTimeOptions)}
              triggerProps={{
                disabled: isLoadingSchedules || isOneTimeClass,
              }}
            />
          </div>
          {from && (
            <>
              <div className="col-span-2">
                <FormRadioGroup label="Teacher" form={form} name="teacherType" options={AllSelectTeacherTypes} />
              </div>
              <div className="col-span-2">{teacherSelect}</div>
              <div className="col-span-2">
                <FormMultiSelect
                  form={form}
                  label={'Students'}
                  name="students"
                  multipleSelectorProps={{
                    placeholder: 'Search students by name to add to the group',
                    onSearch: userTypeaheadSearch(queryClient, { schools: [schoolId].filter(isNotNullish) }),
                  }}
                />
              </div>
            </>
          )}
          <FormInputDate
            name="overrideStartPlainDate"
            label="Manually override start date"
            form={form}
            showClearDateButton={true}
            description={
              selectedSchedule
                ? `Selected schedule [${selectedSchedule.name}] default start date: ${formatPlainDate(selectedSchedule.startPlainDate)}`
                : null
            }
          />
          <FormInputDate
            name="overrideEndPlainDate"
            label="Manually override end date"
            form={form}
            showClearDateButton={true}
            description={
              selectedSchedule
                ? `Selected schedule [${selectedSchedule.name}] default end date: ${formatPlainDate(selectedSchedule.endPlainDate)}`
                : null
            }
          />
          <FormComboBox
            form={form}
            label="Learning path"
            name="learningPath"
            placeholder="Select learning path"
            {...comboBoxOptionsOf(learningPathOptions)}
            triggerProps={{ disabled: isLoadingLearningPaths }}
          />
          <FormComboBox
            form={form}
            label="Starting lesson"
            name="startingLesson"
            placeholder="Select starting lesson"
            {...comboBoxOptionsOf(selectableLessons)}
            triggerProps={{
              disabled: isLoadingLearningPaths,
            }}
          />
        </>
      ) : (
        <>
          <div className="space-y-2 py-4">
            <div className="flex gap-2 items-center">
              <Label className="font-bold">Teacher name</Label>
            </div>
            <p className="text-[#64748B]">{from?.coach ? from.coach.fullNameOrEmail : 'No teacher assigned'}</p>
          </div>
          <div className="col-span-2">
            <FormSingleSelect
              form={form}
              label="Attendance status"
              name="attendanceStatus"
              options={attendanceStatusSelectableValues}
              renderOption={getAttendanceStatusLabel}
              triggerProps={{ className: 'w-2/3' }}
            />
          </div>
          <div className="col-span-2">
            <FormMultiSelect
              form={form}
              name="emailsSent"
              label="Emails sent"
              multipleSelectorProps={{
                options: emailsSentOptions,
                className: 'w-2/3',
              }}
            />
          </div>
          {from?.coachLastUnassignedAtPlainDate && (
            <div className="col-span-2">
              <FormInputDate
                name="coachUnassignedAtPlainDate"
                label="Reassignment date"
                form={form}
                showClearDateButton={true}
              />
            </div>
          )}
          <div className="col-span-2">
            <FormTextArea
              form={form}
              label="Teacher attendance notes"
              name="attendanceNotes"
              textAreaProps={{ className: 'w-2/3 resize-none' }}
            />
          </div>
        </>
      )}
    </div>
  );
};
